/**
 * What is running, without a refresh (owner, 2026-10-06: *"check for aysnc evetns style of what
 * is running wiht a spinner and so on! so we don need to refresh"*).
 *
 * 🔴 ONE POLL OF ONE DATABASE-ONLY FEED, SHARED BY THE WHOLE SHELL. `GET /live` is answered from
 * the database alone (`domain/live.py`, held to it by a parse), so asking it every three seconds
 * costs a few small queries — where a Classroom listing is seconds. Server push was weighed and
 * not taken: the worker itself only looks every 15 seconds, so a stream could not show a run
 * landing sooner than this does, and one open stream per tab would hold a worker thread on a
 * single-process server. The feed's shape is what a stream would send, so the transport can
 * change without a screen changing.
 *
 * ⚠️ HOW OFTEN: every 3 s while anything is in flight or somebody is watching (the tray, the
 * report sheet), every 20 s otherwise, and NOT AT ALL while the tab is hidden — a background tab
 * polling all day is requests nobody read. It asks at once when the tab comes back and when an
 * action calls `poke()`.
 * 🔴 A FAILED POLL KEEPS WHAT WAS THERE. `useAsync` blanks its data on an error, which is right for
 * a one-off read and wrong here: one dropped request would empty every count on the page. This
 * keeps the last answer, says it cannot reach the console, and backs off 3 → 6 → 12 → 30 s.
 * ⚠️ NO CURSOR. The feed carries the last hour's endings and this keeps the ones it has seen, keyed
 * by attempt AND outcome — a held run that is later finalised has two endings, and both are news.
 * What was there at the first answer is history, never announced.
 */
import {
  createContext, useCallback, useContext, useEffect, useReducer, useRef, useState,
  type ReactNode,
} from "react";
import {
  getLive, type ApiError, type LiveFeed, type LiveFinished, type LiveGrouped, type LiveStage,
} from "./api";
import { tracked } from "./useAsync";

export const BUSY_MS = 3_000;
export const IDLE_MS = 20_000;
/** After 1, 2, 3 and 4+ failures in a row. */
export const BACKOFF_MS = [3_000, 6_000, 12_000, 30_000] as const;
/** How many endings a page can still be told about; older ones have been shown or folded. */
const EVENTS_KEPT = 50;

/** One ending the page had not seen before, numbered so each reader can say how far it got. */
export interface LiveEvent { n: number; item: LiveFinished; seenAt: number }
/** A team grouping that ended since the page started listening — numbered on the same counter
 *  as a run's ending, so the two read in one order. */
export interface GroupEvent { n: number; item: LiveGrouped; seenAt: number }

export interface Live {
  feed: LiveFeed | null;
  error: ApiError | null;
  /** When the last answer arrived, on this browser's clock. */
  at: number | null;
  /** The server's clock minus this browser's, so a timer reads the server's minutes. */
  skew: number;
  /** Failed polls in a row; the next try is at `retryAt`. */
  failures: number;
  retryAt: number | null;
  /** A poll is on its way. */
  polling: boolean;
  /** The tab is hidden, so nothing is being asked. */
  hidden: boolean;
  /** New endings, oldest first. */
  events: LiveEvent[];
  grouped: GroupEvent[];
  /** Ask now — after an action the server accepted. */
  poke: () => void;
  /** Ask at the fast cadence while something is watching; returns the release. */
  watch: () => () => void;
}

const noop = () => {};
/** ⚠️ WHAT A PAGE RENDERED OUTSIDE THE SHELL GETS: nothing running, and nothing asked. A test
 *  that draws one page without the provider reads a quiet feed rather than crashing. */
export const IDLE_LIVE: Live = {
  feed: null, error: null, at: null, skew: 0, failures: 0, retryAt: null, polling: false,
  hidden: false, events: [], grouped: [], poke: noop, watch: () => noop,
};

export const LiveContext = createContext<Live>(IDLE_LIVE);
export const useLive = (): Live => useContext(LiveContext);

export function LiveProvider({ value, children }: { value: Live; children: ReactNode }) {
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

const keyOf = (f: LiveFinished) => `${f.attempt_id}\u0000${f.outcome}`;

/** Anything in flight, so the next ask is soon. */
export const busy = (f: LiveFeed | null): boolean =>
  Boolean(f && (f.counts.grading + f.counts.queued + f.counts.other) > 0);

/** How long to wait before the next ask. */
export function nextDelay(f: LiveFeed | null, failures: number, watching: boolean): number {
  if (failures > 0) return BACKOFF_MS[Math.min(failures, BACKOFF_MS.length) - 1]!;
  return busy(f) || watching ? BUSY_MS : IDLE_MS;
}

export function useLiveFeed(courseId: string): Live {
  const [feed, setFeed] = useState<LiveFeed | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const [skew, setSkew] = useState(0);
  const [failures, setFailures] = useState(0);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [polling, setPolling] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [grouped, setGrouped] = useState<GroupEvent[]>([]);

  const timer = useRef<number | null>(null);
  const inflight = useRef(false);
  const again = useRef(false);
  const watchers = useRef(0);
  const fails = useRef(0);
  const last = useRef<LiveFeed | null>(null);
  // `null` until the first answer: what is there then is history, not news.
  const seen = useRef<Set<string> | null>(null);
  const seenGrouped = useRef<Set<string> | null>(null);
  const counter = useRef(0);
  // ⚠️ A STALE ANSWER MUST NOT LAND ON ANOTHER COHORT — the `useAsync` sequence guard, one level
  // up: a cohort switch starts a new generation and anything older is dropped.
  const gen = useRef(0);
  const first = useRef(true);
  const pollRef = useRef<() => void>(noop);

  const clear = () => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
  };
  const schedule = useCallback(() => {
    clear();
    if (document.hidden) { setRetryAt(null); return; }
    const ms = nextDelay(last.current, fails.current, watchers.current > 0);
    setRetryAt(Date.now() + ms);
    timer.current = window.setTimeout(() => pollRef.current(), ms);
  }, []);

  const poll = useCallback(() => {
    if (inflight.current) { again.current = true; return; }
    clear();
    inflight.current = true;
    setPolling(true);
    const mine = gen.current;
    // ⚠️ ONLY THE FIRST ASK COUNTS TOWARDS `data-tb-inflight`: it is what a page needs before it
    // can be said to have settled. Every later poll is background, and counting them would make
    // a busy cohort's page never settle for a browser check.
    const req = first.current ? tracked(getLive(courseId)) : getLive(courseId);
    first.current = false;
    req.then((f) => {
      if (mine !== gen.current) return;
      const keys = f.finished.map(keyOf);
      if (seen.current === null) {
        seen.current = new Set(keys);
      } else {
        const fresh = f.finished.filter((x) => !seen.current!.has(keyOf(x))).reverse();
        if (fresh.length) {
          const now = Date.now();
          for (const x of fresh) seen.current.add(keyOf(x));
          setEvents((ev) => [...ev, ...fresh.map((item) => ({ n: ++counter.current, item,
                                                             seenAt: now }))]
            .slice(-EVENTS_KEPT));
        }
      }
      // A grouping's ending, by the same rule: what is there at the first answer is history.
      const groups = f.grouped ?? [];
      if (seenGrouped.current === null) {
        seenGrouped.current = new Set(groups.map((g) => g.id));
      } else {
        const fresh = groups.filter((g) => !seenGrouped.current!.has(g.id)).reverse();
        if (fresh.length) {
          const now = Date.now();
          for (const g of fresh) seenGrouped.current.add(g.id);
          setGrouped((ev) => [...ev, ...fresh.map((item) => ({ n: ++counter.current, item,
                                                              seenAt: now }))]
            .slice(-EVENTS_KEPT));
        }
      }
      last.current = f;
      fails.current = 0;
      setFeed(f); setError(null); setFailures(0); setAt(Date.now());
      const server = Date.parse(f.now);
      setSkew(Number.isFinite(server) ? server - Date.now() : 0);
    }).catch((e: unknown) => {
      if (mine !== gen.current) return;
      fails.current += 1;
      setError(e as ApiError); setFailures(fails.current);
    }).finally(() => {
      if (mine !== gen.current) return;
      inflight.current = false;
      setPolling(false);
      if (again.current) { again.current = false; pollRef.current(); } else schedule();
    });
  }, [courseId, schedule]);
  pollRef.current = poll;

  useEffect(() => {
    gen.current += 1;
    inflight.current = false; again.current = false; fails.current = 0;
    last.current = null; seen.current = null; seenGrouped.current = null; first.current = true;
    setFeed(null); setError(null); setFailures(0); setAt(null); setEvents([]); setGrouped([]);
    if (!document.hidden) pollRef.current();
    const onVisible = () => {
      setHidden(document.hidden);
      if (document.hidden) { clear(); setRetryAt(null); } else pollRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clear();
      gen.current += 1;
    };
  }, [courseId]);

  const poke = useCallback(() => pollRef.current(), []);
  const watch = useCallback(() => {
    watchers.current += 1;
    // Somebody started watching while the next ask is twenty seconds away: bring it forward.
    if (!inflight.current) schedule();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      watchers.current = Math.max(0, watchers.current - 1);
      // And back to the slow cadence as soon as nobody is: the fast ask already waiting would
      // otherwise go on firing for whoever closed the tray.
      if (!inflight.current) schedule();
    };
  }, [schedule]);

  return { feed, error, at, skew, failures, retryAt, polling, hidden, events, grouped, poke,
           watch };
}

// ── one clock for every timer on the page ──────────────────────────────────────────────────

/**
 * ⚠️ ONE INTERVAL, HOWEVER MANY TIMERS. A page with forty runs in flight would otherwise start
 * forty intervals; this re-renders the components that show a timer once a second, and stops
 * when none is mounted.
 */
const ticking = new Set<() => void>();
let tickId: number | null = null;

export function useNow(): number {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    ticking.add(force);
    if (tickId === null) tickId = window.setInterval(() => ticking.forEach((f) => f()), 1000);
    return () => {
      ticking.delete(force);
      if (ticking.size === 0 && tickId !== null) { window.clearInterval(tickId); tickId = null; }
    };
  }, []);
  return Date.now();
}

/** "3:12", "1:04:09" — how long since a moment, on the server's clock. */
export function elapsed(sinceIso: string | null | undefined, now: number, skew: number): string {
  if (!sinceIso) return "";
  const t = Date.parse(sinceIso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.floor((now + skew - t) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/** A running timer: a span that ticks, in the mono figures every count here uses. */
export function Elapsed({ since }: { since: string | null | undefined }) {
  const now = useNow();
  const { skew } = useLive();
  const text = elapsed(since, now, skew);
  return text ? <span className="live-t">{text}</span> : null;
}

/** The clock time of a moment in this viewer's zone — "11:40". */
export function clock(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** "next", "2nd", "3rd", "11th" — a place in the queue. */
export function place(n: number): string {
  if (n <= 1) return "next";
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th"
    : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

/**
 * What a run's timer counts from: the wait from when it was sent, and the grading from when a
 * grader took it (2026-10-06) — so time spent queued behind a busy grader is never counted as
 * grading. A run with no recorded start counts from when it was sent, as it always did.
 */
export function since(r: { stage: LiveStage; started_at: string | null;
                           running_since?: string | null }): string | null {
  return r.stage === "waiting" || r.stage === "starting" ? r.started_at
    : (r.running_since ?? r.started_at);
}

/** What a stage is called on every screen that shows one. */
export const STAGE_WORDS: Record<LiveStage, string> = {
  starting: "Starting…",
  waiting: "Waiting for a grader",
  grading: "Grading",
  checking: "Checking the citations",
  held: "Retrying the check",
};

/** A spinner, decorative: the words beside it carry the state. */
export const Spin = ({ dim = false }: { dim?: boolean }) =>
  <span className={dim ? "spin dim" : "spin"} aria-hidden="true" />;
